import { memo, SyntheticEvent, useMemo, useRef, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Divider,
    Button,
    FormControlLabel,
    Checkbox,
    DialogContentText,
    Box,
    Tooltip,
    styled,
    TooltipProps,
    tooltipClasses,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { addNotification } from '../../../notification/slices/notificationSlice';
import { NotificationType } from '../../../notification/types';
import { useGetEConsentTextQuery } from '../../api/eConsentPlatformApi';
import { useUpdateEConsentStatusMutation } from '../../api/eConsentPharmaNavigatorApi';
import { EConsentWrapper } from '../../index';
import { setConsentState } from '../../api/eConsentPlatformSlice';
import { RootState } from '../../../../store';
import ESignature, { ESignatureHandle } from '../../../e-signature';

const SCROLL_THRESHOLD = 5;

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} classes={{ popper: className }} />
))({
    [`& .${tooltipClasses.tooltip}`]: {
        maxWidth: 340,
    },
});

const EConsentDialog = () => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const [eConsentAccepted, updateEConsent] = useState(false);
    const [isSignatureEmpty, setIsSignatureEmpty] = useState(true);
    const [isScrollable, setIsScrollable] = useState(false);
    const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
    const canvasRef = useRef<ESignatureHandle | null>(null);
    const { isLoggedIn } = useSelector((state: RootState) => state.auth);
    const { data } = useGetEConsentTextQuery(undefined, { skip: !isLoggedIn });
    const [updateEConsentStatus] = useUpdateEConsentStatusMutation();
    const navigate = useNavigate();

    const content = data?.content || '';

    const toggleCheckbox = () => {
        updateEConsent((prevState) => !prevState);
    };

    const onSubmit = async () => {
        const signatureBlob = await canvasRef.current?.getSignatureBlob();

        if (!data || !signatureBlob) return;

        const formData = new FormData();
        formData.append('File', signatureBlob, 'signature.png');
        formData.append('EConsentDocumentId', String(data.id));

        const { error } = await updateEConsentStatus(formData);

        if (error) {
            dispatch(addNotification({ message: t('common-error-message'), type: NotificationType.ERROR }));
        } else {
            dispatch(setConsentState(true));
            setTimeout(() => navigate('/'), 500);
        }
    };

    const handleScroll = (event: SyntheticEvent) => {
        const { scrollHeight, clientHeight, scrollTop } = event.currentTarget;

        const isAtBottom = scrollHeight - scrollTop - clientHeight < SCROLL_THRESHOLD;
        setIsScrolledToBottom(isAtBottom);
    };

    const setIfScrollable = (eConsentContentRef: HTMLDivElement) => {
        if (!eConsentContentRef) return;
        const { scrollHeight, clientHeight } = eConsentContentRef;
        const isScrollable = scrollHeight > clientHeight;
        setIsScrollable(isScrollable);
    };

    const isSubmitDisabled = !eConsentAccepted || isSignatureEmpty || (isScrollable && !isScrolledToBottom);

    const tooltipMessages = useMemo(
        () =>
            [
                isScrollable && !isScrolledToBottom && t('consent-acceptance-requirements.review-terms'),
                isSignatureEmpty && t('consent-acceptance-requirements.sign'),
                !eConsentAccepted && t('consent-acceptance-requirements.agree-to-terms'),
            ].filter(Boolean),
        [isScrolledToBottom, isSignatureEmpty, eConsentAccepted, isScrollable, t]
    );

    const tooltipText =
        tooltipMessages.length > 0 ? `${tooltipMessages.length > 1 ? '• ' : ''}${tooltipMessages.join('\n• ')}` : '';

    return (
        <Dialog
            data-testid='e-consent-dialog'
            sx={{
                background: 'rgba(0, 0, 0, 0.6)}',
                width: '100%',
                '& .MuiDialog-container': {
                    '& .MuiPaper-root': {
                        padding: '8px 0 16px',
                        width: '100%',
                        maxWidth: '426px',
                        '@media (max-width: 480px)': {
                            maxWidth: '100%',
                            margin: 0,
                            maxHeight: '100%',
                            borderRadius: '0',
                            height: '100vh',
                        },
                    },
                },
            }}
            open={true}
            aria-labelledby='e-consent-dialog-title'
            aria-describedby='e-consent-dialog-description'
        >
            <DialogTitle id='e-consent-dialog-title' component='div'>
                <Typography variant='h2' data-testid='dialog-title'>
                    {t('welcome-e-consent')}
                </Typography>
                <Typography variant='subtitle1' sx={{ mt: 2 }} data-testid='dialog-subtitle'>
                    {t('e-consent-description')}
                </Typography>
                <Divider sx={{ mt: 2 }} />
            </DialogTitle>
            <DialogContent
                color='common.grey1'
                ref={setIfScrollable}
                onScroll={isScrolledToBottom ? undefined : handleScroll}
            >
                <EConsentWrapper content={content} />
            </DialogContent>

            <Divider sx={{ marginBottom: '14px' }} />
            <Box sx={{ padding: '0 24px 8px' }}>
                <ESignature ref={canvasRef} setIsSignatureEmpty={setIsSignatureEmpty} />
            </Box>

            <DialogContentText component='div'>
                <FormControlLabel
                    sx={{ marginLeft: '12px' }}
                    control={
                        <Checkbox
                            data-testid='e-consent-checkbox'
                            checked={eConsentAccepted}
                            onClick={toggleCheckbox}
                            color='secondary'
                            sx={{
                                color: 'common.themes.blue.primary',
                                '&.Mui-checked': {
                                    color: 'common.themes.blue.primary',
                                },
                            }}
                        />
                    }
                    label={t('agree-e-consent')}
                    slotProps={{ typography: { variant: 'subtitle1', color: 'common.black' } }}
                />
            </DialogContentText>

            <DialogActions
                sx={{
                    justifyContent: 'center',
                    padding: '16px 24px',
                }}
            >
                <StyledTooltip
                    title={tooltipText}
                    placement='top'
                    slotProps={{
                        popper: {
                            sx: {
                                whiteSpace: 'pre-line',
                                lineHeight: '20px',
                                '& .MuiTooltip-tooltip': { width: '500px' },
                            },
                        },
                    }}
                >
                    <Box width={1}>
                        <Button
                            onClick={onSubmit}
                            variant='contained'
                            disabled={isSubmitDisabled}
                            data-testid='submit-button'
                            sx={{
                                maxWidth: '378px',
                                width: '100%',
                                height: '48px',
                                color: '#fff',
                                bgcolor: 'common.themes.blue.primary',
                                '&:hover': {
                                    bgcolor: 'common.themes.blue.primaryHover',
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: 'common.themes.blue.primary',
                                    opacity: 0.7,
                                    color: 'white',
                                },
                            }}
                        >
                            {t('submit')}
                        </Button>
                    </Box>
                </StyledTooltip>
            </DialogActions>
        </Dialog>
    );
};

export default memo(EConsentDialog);
